import React, { useState, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import TaskDetailModal from "./TaskDetailModal";
import GroupSection, { GroupItem } from "./GroupSection";
import CollapsibleGroup from "./CollapsibleGroup";
import TaskDetailSidebar from "./TaskDetailSidebar";
import "./TaskManagement.scss";
import LeadOverviewFilters from "features/leadOverview/components/LeadOverviewFilters";
import AddClientSidebar from "./AddClientSidebar";

import { Product } from "types/product";
import { LeadType } from "types/leadType";

// Neues Interface für Subtasks
export interface SubTask {
  id: string;
  title: string;
  completed: boolean;
}

// Definition des Task-Interfaces – zusätzlich mit Produkt, Standort, Subtasks und dealAmount in €
export interface Task {
  id: string;
  title: string;
  status: "todo" | "inProgress" | "done" | "lost";
  priority: "low" | "medium" | "high";
  category: "Kurzzeitpflege" | "Vollstationäre Pflege" | "Pflegedienst" | "Immobilienverrentung" | "Tagespflege" | "24 Stunden Betreuung" | "Betreutes Wohnen" | "Badsanierung" | "Hospiz" | "Sitzlift";
  dueDate?: string;
  description?: string;
  assignedTo?: string;
  product?: string;
  location?: string;
  subtasks?: SubTask[];
  dealAmount: number;
}

// Platzhalterdaten für SalesManagement (Vertriebsfragen mit Produktbedarf)
// Es wurden Beispielwerte für den dealAmount (in €) eingefügt.
const initialSalesManagementTasks: Task[] = [
  {
    id: "101",
    title: "Max Mustermann",
    status: "inProgress",
    priority: "high",
    category: "Pflegedienst",
    dueDate: "2025-04-08",
    description: "Termin vorbereiten",
    assignedTo: "Peter Meier",
    product: "Pflegegrad 3",
    location: "Berlin",
    dealAmount: 8000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: true },
      { id: "3", title: "Termin vorbereiten", completed: false }
    ]
  },
  {
    id: "102",
    title: "Peter Schneider",
    status: "todo",
    priority: "medium",
    category: "Immobilienverrentung",
    dueDate: "2025-04-11",
    description: "Firma Schmidt benötigt Information zu Produktbedarf B.",
    assignedTo: "Peter Meier",
    product: "Pflegegrad 2",
    location: "Stuttgart",
    dealAmount: 20000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: false },
      { id: "2", title: "Termin vereinbaren ", completed: false },
      { id: "3", title: "Termin vorbereiten", completed: false }
    ]
  },
  {
    id: "103",
    title: "Lukas Schmidt",
    status: "inProgress",
    priority: "low",
    category: "Tagespflege",
    dueDate: "2025-03-31",
    description: "Abschlussgespräch zu Produktbedarf A.",
    assignedTo: "Hans Schmidt",
    product: "Pflegegrad 1",
    location: "Dortmund",
    dealAmount: 4500,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: false },
      { id: "3", title: "Termin vorbereiten", completed: false }
    ]
  },
  {
    id: "104",
    title: "Hans Müller",
    status: "todo",
    priority: "medium",
    category: "24 Stunden Betreuung",
    dueDate: "2025-04-13",
    description: "Erstkontakt zu Produktbedarf C.",
    assignedTo: "Petra Schmidt",
    product: "Pflegegrad 2",
    location: "München",
    dealAmount: 18000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: false },
      { id: "2", title: "Termin vereinbaren ", completed: false },
      { id: "3", title: "Termin vorbereiten", completed: false }
    ]
  },
  {
    id: "105",
    title: "Laura Fischer",
    status: "done",
    priority: "high",
    category: "Betreutes Wohnen",
    dueDate: "2025-04-15",
    description: "Vertragsverhandlung zu Produktbedarf D.",
    assignedTo: "Hans Schmidt",
    product: "Pflegegrad 3",
    location: "Hamburg",
    dealAmount: 3000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: true },
      { id: "3", title: "Termin vorbereiten", completed: true }
    ]
  },
  {
    id: "106",
    title: "Thomas Weber",
    status: "done",
    priority: "medium",
    category: "Badsanierung",
    dueDate: "2025-04-20",
    description: "Abschlussgespräch zu Produktbedarf E.",
    assignedTo: "Maria Müller",
    product: "Pflegegrad 1",
    location: "Frankfurt",
    dealAmount: 25000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: true },
      { id: "3", title: "Termin vorbereiten", completed: true }
    ]
  },
  {
    id: "107",
    title: "Julia Schneider",
    status: "lost",
    priority: "high",
    category: "Hospiz",
    dueDate: "2025-04-25",
    description: "Erstkontakt zu Produktbedarf F.",
    assignedTo: "Hans Schmidt",
    product: "Pflegegrad 2",
    location: "Berlin",
    dealAmount:7000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: true },
      { id: "3", title: "Termin vorbereiten", completed: true }
    ]
  },
  {
    id: "108",
    title: "Anna Schmidt",
    status: "lost",
    priority: "medium",
    category: "Sitzlift",
    dueDate: "2025-04-30",
    description: "Vertragsverhandlung zu Produktbedarf G.",
    assignedTo: "Petra Schmidt",
    product: "Pflegegrad 3",
    location: "Stuttgart",
    dealAmount: 6000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: true },
      { id: "3", title: "Termin vorbereiten", completed: true }
    ]
  },
  {
    id: "109",
    title: "Sophie Hoffmann",
    status: "lost",
    priority: "high",
    category: "Kurzzeitpflege",
    dueDate: "2025-05-05",
    description: "Abschlussgespräch zu Produktbedarf H.",
    assignedTo: "Peter Meier",
    product: "Pflegegrad 1",
    location: "München",
    dealAmount: 4500,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: true },
      { id: "3", title: "Termin vorbereiten", completed: true }
    ]
  },
  {
    id: "110",
    title: "Erika Musterfrau",
    status: "lost",
    priority: "medium",
    category: "Kurzzeitpflege",
    dueDate: "2025-05-10",
    description: "Erstkontakt zu Produktbedarf I.",
    assignedTo: "Hans Schmidt",
    product: "Pflegegrad 2",
    location: "Frankfurt",
    dealAmount: 5500,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: true },
      { id: "2", title: "Termin vereinbaren ", completed: true },
      { id: "3", title: "Termin vorbereiten", completed: true }
    ]
  },
  {
    id: "111",
    title: "Maximilian Becker",
    status: "todo",
    priority: "high",
    category: "Pflegedienst",
    dueDate: "2025-05-15",
    description: "Vertragsverhandlung zu Produktbedarf J.",
    assignedTo: "Maria Müller",
    product: "Pflegegrad 3",
    location: "Hamburg",
    dealAmount: 9000,
    subtasks: [
      { id: "1", title: "Klient anrufen", completed: false },
      { id: "2", title: "Termin vereinbaren ", completed: false },
      { id: "3", title: "Termin vorbereiten", completed: false }
    ]
  }
];

const ItemTypes = {
  TASK: "task"
};

interface DragItem {
  id: string;
  status: Task["status"];
}

interface TaskCardProps {
  task: Task;
  onClick: (task: Task) => void;
  getStatusColor: (status: string) => string;
  updateSubTask: (taskId: string, subTaskId: string, completed: boolean) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick, getStatusColor, updateSubTask }) => {
  const [{ isDragging }, drag] = useDrag<DragItem, unknown, { isDragging: boolean }>({
    type: ItemTypes.TASK,
    item: { id: task.id, status: task.status },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const handleSubtaskChange = (subTaskId: string, currentStatus: boolean) => {
    updateSubTask(task.id, subTaskId, !currentStatus);
  };

  return (
    <div
      ref={drag}
      className="task-card"
      style={{ opacity: isDragging ? 0.5 : 1, cursor: "move" }}
      onClick={() => onClick(task)}
    >
      <div className="task-priority" style={{ backgroundColor: getStatusColor(task.status) }} />
      <h4>{task.title}</h4>
      <p className="deal-amount">Umsatz: {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(task.dealAmount)}</p>
      <div className="task-meta">
        <span className="category-tag">
          <span className={`priority-dot ${task.priority}`}></span>
          {task.category}
        </span>
        {task.dueDate && (
          <span>
            <i className="fa-regular fa-calendar"></i> {task.dueDate}
          </span>
        )}
        <span className="category-tag">{task.product}</span>
      </div>
      {task.assignedTo && (
        <div className="assignee-icon">
          {task.assignedTo
            .split(" ")
            .map((namePart) => namePart[0])
            .join("")}
        </div>
      )}      {/* Subtask-Checklist */}
      {task.subtasks && (
        <div className="subtask-checklist">
          {task.subtasks.map((sub) => (
            <div key={sub.id} className="subtask-item">
              <input
                type="checkbox"
                checked={sub.completed}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSubtaskChange(sub.id, sub.completed);
                }}
              />
              <span className={sub.completed ? "task-row-done" : ""}>{sub.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface ColumnProps {
  status: Task["status"];
  tasks: Task[];
  header: string;
  onDropTask: (id: string, newStatus: Task["status"]) => void;
  onTaskClick: (task: Task) => void;
  getStatusColor: (status: string) => string;
  updateSubTask: (taskId: string, subTaskId: string, completed: boolean) => void;
}

const Column: React.FC<ColumnProps> = ({ status, tasks, header, onDropTask, onTaskClick, getStatusColor, updateSubTask }) => {
  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: ItemTypes.TASK,
    drop: (item: DragItem) => {
      if (item.status !== status) {
        onDropTask(item.id, status);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  // Summe aller dealAmount in dieser Spalte
  const totalDealAmount = tasks.reduce((total, task) => total + task.dealAmount, 0);

  return (
    <div
      ref={drop}
      className="kanban-column"
      style={{
        borderTop: `4px solid ${getStatusColor(status)}`,
        backgroundColor: isOver && canDrop ? "#f0f0f0" : "inherit"
      }}
    >
      <div className="kanban-column-header">
        <h3>{header}</h3>
        <span className="deal-count">{tasks.length}</span>
      </div>
      <div className="kanban-column-body">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onClick={onTaskClick}
            getStatusColor={getStatusColor}
            updateSubTask={updateSubTask}
          />
        ))}
      </div>
      <div className="column-footer">
        Gesamt: {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(totalDealAmount)}
      </div>
    </div>
  );
};

interface BoardProps {
  tasks: Task[];
  columnHeaders: { [key in Task["status"]]: string };
  onDropTask: (id: string, newStatus: Task["status"]) => void;
  onTaskClick: (task: Task) => void;
  getStatusColor: (status: string) => string;
  updateSubTask: (taskId: string, subTaskId: string, completed: boolean) => void;
}

const Board: React.FC<BoardProps> = ({ tasks, columnHeaders, onDropTask, onTaskClick, getStatusColor, updateSubTask }) => {
  const statuses = Object.keys(columnHeaders) as Task["status"][];
  return (
    <div className="kanban-board">
      {statuses.map((status) => (
        <Column
          key={status}
          status={status}
          tasks={tasks.filter((task) => task.status === status)}
          header={columnHeaders[status]}
          onDropTask={onDropTask}
          onTaskClick={onTaskClick}
          getStatusColor={getStatusColor}
          updateSubTask={updateSubTask}
        />
      ))}
    </div>
  );
};

/* Gemeinsamer Hook zur Verwaltung */
const useTaskManagement = (initialData: Task[]) => {
  const [tasks, setTasks] = useState<Task[]>(initialData);
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [responsibilityFilter, setResponsibilityFilter] = useState<string>("all");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const addOrUpdateTask = (task: Task) => {
    setTasks((prev) => {
      const existingIndex = prev.findIndex((t) => t.id === task.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = task;
        return updated;
      }
      return [...prev, { ...task, id: Date.now().toString() }];
    });
  };

  const updateTaskStatus = useCallback((id: string, newStatus: Task["status"]) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t))
    );
  }, []);

  // Neue Funktion zum Updaten eines Subtasks
  const updateSubTask = (taskId: string, subTaskId: string, completed: boolean) => {
    setTasks((prev) =>
      prev.map((task) => {
        if (task.id === taskId && task.subtasks) {
          const newSubtasks = task.subtasks.map((sub) =>
            sub.id === subTaskId ? { ...sub, completed } : sub
          );
          return { ...task, subtasks: newSubtasks };
        }
        return task;
      })
    );
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesPriority = priorityFilter === "all" || task.priority === priorityFilter;
    const matchesCategory = categoryFilter === "all" || task.category === categoryFilter;
    const matchesResponsibility =
      responsibilityFilter === "all" || task.assignedTo === responsibilityFilter;
    const matchesProduct = productFilter === "all" || task.product === productFilter;
    const matchesLocation = locationFilter === "all" || task.location === locationFilter;
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesPriority && matchesCategory && matchesResponsibility && matchesProduct && matchesLocation && matchesSearch;
  });

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "todo":
        return "#ff6b6b";
      case "inProgress":
        return "#4d8ba5";
      case "done":
        return "#98c44c";
      case "lost":
        return "#ffcc00";
      default:
        return "#ddd";
    }
  };

  const getQuickAccessItems = (category: "Kurzzeitpflege" | "Vollstationäre Pflege" | "Pflegedienst"): GroupItem[] => {
    return tasks
      .filter((t) => t.category === category && t.status === "todo")
      .map((t) => ({
        title: t.title,
        detail: t.dueDate ? `Fällig am ${t.dueDate}` : "",
      }));
  };

  return {
    tasks,
    filteredTasks,
    addOrUpdateTask,
    updateTaskStatus,
    updateSubTask,
    priorityFilter,
    setPriorityFilter,
    categoryFilter,
    setCategoryFilter,
    responsibilityFilter,
    setResponsibilityFilter,
    productFilter,
    setProductFilter,
    locationFilter,
    setLocationFilter,
    searchQuery,
    setSearchQuery,
    isModalOpen,
    setModalOpen,
    selectedTask,
    setSelectedTask,
    getStatusColor,
    getQuickAccessItems
  };
};

// SalesManagement-Komponente (Vertriebsanfragen mit Produktbedarf)
export const SalesManagement: React.FC = () => {
  const {
    filteredTasks,
    updateTaskStatus,
    updateSubTask,
    setSelectedTask,
    getStatusColor,
    searchQuery,
    setSearchQuery,
    priorityFilter,
    setPriorityFilter,
    categoryFilter,
    setCategoryFilter,
    responsibilityFilter,
    setResponsibilityFilter,
    productFilter,
    setProductFilter,
    locationFilter,
    setLocationFilter,
    isModalOpen,
    setModalOpen,
    selectedTask,
    addOrUpdateTask,
    getQuickAccessItems
  } = useTaskManagement(initialSalesManagementTasks);

  const columnHeaders = {
    todo: "Neu",
    inProgress: "In Bearbeitung",
    done: "Gewonnen",
    lost: "Geschlossen"
  };

  const [selectedStatusItems, setSelectedStatusItems] = useState<number[]>([]);
  const [selectedLeadTypeItems, setSelectedLeadTypeItems] = useState<number[]>([]);
  const [selectedProductItems, setSelectedProductItems] = useState<Product[]>([]);
  const [nameSearchText, setNameSearchText] = useState<string>("");
  const [anfragenNumberSearchText, setAnfragenNumberSearchText] = useState<string>("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="detail-layout-2">
        <div className="lead-container">
          <h1 className="text-blue mb-50">Klienten verwalten</h1>
          <button
            className="kanban-switch-green"
            onClick={() => {
              setModalOpen(true);
              setSelectedTask(null);
            }}
          >
            <i className="fa-solid fa-plus"></i> Klient erstellen
          </button>
          <LeadOverviewFilters
            setPage={setPage}
            onStatusChange={(statuses: number[]) => setSelectedStatusItems(statuses)}
            onLeadTypeChange={(leadTypes: LeadType[]) => setSelectedLeadTypeItems(leadTypes)}
            onProductChange={(products: Product[]) => setSelectedProductItems(products)}
            onNameSearchChange={(searchText: string) => setNameSearchText(searchText)}
            onAnfragenNumberSearchChange={(searchText: string) => setAnfragenNumberSearchText(searchText)}
          />
          <Board
            tasks={filteredTasks}
            columnHeaders={columnHeaders}
            onDropTask={updateTaskStatus}
            onTaskClick={setSelectedTask}
            getStatusColor={getStatusColor}
            updateSubTask={updateSubTask}
          />
        </div>
        {selectedTask && (
          <TaskDetailSidebar
            selectedTask={selectedTask}
            getQuickAccessItems={getQuickAccessItems}
            onEdit={() => setModalOpen(true)}
            onClose={() => setSelectedTask(null)}
          />
        )}
      </div>
      {isModalOpen && (
        <AddClientSidebar onClose={() => setModalOpen(false)} onSave={() => setModalOpen(false)} />
      )}
    </DndProvider>
  );
};
